import { Box, CircularProgress, Tab, Typography } from "@mui/material";
import { graphql } from "babel-plugin-relay/macro";
import { Suspense, useContext } from "react";
import { useLazyLoadQuery } from "react-relay";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { SettingsContainerQuery } from "./__generated__/SettingsContainerQuery.graphql";
import SectionTabs from "../../../components/SectionTabs";
import CurrentUserContext from "../../../contexts/CurrentUserContext";
import { SectionType, SidepaneConfig } from "../../../utils/constants";
import nullthrows from "../../../utils/nullthrows";
import FacebookTab from "./FacebookTab";
import IntegrationsTab from "./IntegrationsTab";
import InternationalizationTab from "./InternationalizationTab";
import OrganizationTab from "./OrganizationTab";
import ShopifyTab from "./ShopifyTab";
import BrandAdminTab from "./admin/BrandAdminTab";
import BillingTab from "./billing/BillingTab";

const query = graphql`
  query SettingsContainerQuery($id: ID!) {
    user {
      isInternal
    }
    brand(id: $id) {
      ...BrandAdminTab_brand
      ...OrganizationTab_brand
      ...ShopifyTab_brand
      ...FacebookTab_brand
      ...InternationalizationTab_brand
      finishedOnboarding
    }
  }
`;

const config = SidepaneConfig.find(
  (config) => config.sectionType === SectionType.SETTINGS
);

const SettingsContainer = () => {
  const currentUserContext = useContext(CurrentUserContext);
  const data = useLazyLoadQuery<SettingsContainerQuery>(query, {
    id: currentUserContext?.activeBrandID ?? "",
  });
  const brand = data.brand;
  const user = data.user;
  const brandFinishedOnboarding = data.brand.finishedOnboarding === true;

  const location = useLocation();
  const navigate = useNavigate();
  const tabs = config?.sections
    .filter((config) => config.isPublic || user?.isInternal === true)
    .map((config, index) => (
      <Tab
        disabled={config.isDisabledPreOnboarded && !brandFinishedOnboarding}
        value={"/" + SectionType.SETTINGS + config.pathname}
        label={config.title}
        key={index}
      />
    ));

  const pathSplit = location.pathname.split("/");
  return (
    <Box sx={{ maxWidth: "100%", overflowX: "hidden" }}>
      <Typography variant="h4">{config?.sectionName}</Typography>
      <SectionTabs
        value={"/" + pathSplit[1] + "/" + pathSplit[2]}
        onChange={(_, value) => {
          navigate(value);
        }}
      >
        {tabs}
      </SectionTabs>
      <Routes>
        {config?.sections
          .filter((config) => {
            return config.isPublic || user?.isInternal === true;
          })
          .map((config, index) => {
            if (config.isDisabledPreOnboarded && !brandFinishedOnboarding) {
              return null;
            }

            let element = null;

            switch (config.pathname) {
              case "/admin":
                element = (
                  <BrandAdminTab
                    brand={brand}
                    key={currentUserContext?.activeBrandID}
                  />
                );
                break;
              case "/organization":
                element = (
                  <OrganizationTab
                    brand={brand}
                    key={currentUserContext?.activeBrandID}
                  />
                );
                break;
              case "/integrations":
                element = (
                  <IntegrationsTab
                    brandID={nullthrows(currentUserContext?.activeBrandID)}
                    key={currentUserContext?.activeBrandID}
                  />
                );
                break;
              case "/internationalization":
                element = (
                  <InternationalizationTab
                    brand={brand}
                    key={currentUserContext?.activeBrandID}
                  />
                );
                break;
              case "/shopify":
                element = (
                  <ShopifyTab
                    brand={brand}
                    key={currentUserContext?.activeBrandID}
                  />
                );
                break;
              case "/meta":
                element = <FacebookTab brand={brand} />;
                break;
              case "/billing":
                element = (
                  <BillingTab key={currentUserContext?.activeBrandID} />
                );
                break;
            }
            return (
              <Route
                path={config.pathname + "/*"}
                element={
                  <Suspense
                    fallback={
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          width: "100%",
                        }}
                      >
                        <CircularProgress />
                      </div>
                    }
                  >
                    {element}
                  </Suspense>
                }
                key={index}
              />
            );
          })}
      </Routes>
    </Box>
  );
};

export default SettingsContainer;
